import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileUploader } from "@/components/ui/file-uploader";
import { MutingAudio } from "@/components/shared/muting-media";
import { useState, useRef, useEffect } from "react";

interface AudioDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  currentAudioUrl?: string;
  title: string;
  maxSize?: number; // Size in MB
}

export function AudioDialog({
  open,
  onClose,
  onUpload,
  currentAudioUrl,
  title,
  maxSize = 10, // Default to 10MB for audio
}: AudioDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  // Force audio element to reload when source changes
  useEffect(() => {
    if (audioRef.current && (previewUrl || currentAudioUrl)) {
      const audio = audioRef.current;
      audio.load();

      // Ensure audio is properly configured
      audio.volume = 1.0; // Full volume
      audio.muted = false; // Not muted
    }
  }, [previewUrl, currentAudioUrl]);

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      await onUpload(selectedFile);
      onClose();
    } catch (error) {
      console.error("Error uploading profile audio:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Choose a new audio file to upload. Supported format: MP3. Maximum
            size: {maxSize}MB
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-6 py-4">
          {(previewUrl || currentAudioUrl) && (
            <div className="w-full space-y-2">
              <MutingAudio
                ref={audioRef}
                src={previewUrl || currentAudioUrl}
                className="w-full"
                controls
                preload="metadata"
              >
                Your browser does not support the audio tag.
              </MutingAudio>
              {selectedFile ? (
                <p className="text-sm text-gray-600">
                  Selected: {selectedFile.name} (
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                </p>
              ) : currentAudioUrl ? (
                <p className="text-sm text-gray-600">Current audio file</p>
              ) : null}
            </div>
          )}
          <FileUploader
            onFileSelect={handleFileSelect}
            accept="audio/mp3,audio/mpeg"
            maxSize={maxSize * 1024 * 1024} // Convert MB to bytes
            buttonText={
              selectedFile ? "Choose Different Audio" : "Choose Audio"
            }
            variant="outline"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
